import React, { useState } from 'react';
import { gql } from '@soundxyz/gql-string';
import {
  ERROR_TYPE,
  FEATURES,
  MISCELLANEOUS_ERROR_ACTIONS,
} from '@soundxyz/utils/constants/logging';
import {
  CollectorsSort,
  CollectorsSortChips,
  Container,
  Heading,
  Wrapper,
} from '@components/Collectors';
import {
  CollectorsSection,
  NUM_BACKED_ENTITIES_PREVIEW,
} from '@components/Collectors/CollectorsSection';
import { SeeAllLink } from '@components/basics/SeeAll';
import type { FragmentType, VariablesOf } from '@graphql/reactQuery';
import {
  BackedArtistsDocument,
  BackedArtistSectionFragmentDoc,
  getFragment,
  prefetchQuery,
  useQuery,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment BackedArtistSection on User {
    id
    webappUri
    ...SeeAllUserInfo
  }

  query BackedArtists($id: UUID!, $pagination: ArtistCollectorCursorConnectionArgs) {
    user(id: $id) {
      id
      backedArtists(pagination: $pagination) {
        pageInfo {
          hasNextPage
        }
        edges {
          node {
            id
            ...CollectorCardInfo
          }
        }
      }
    }
  }
`);

export const ArtistsBacked = ({ user }: { user: FragmentType<BackedArtistSectionFragmentDoc> }) => {
  const userData = getFragment(BackedArtistSectionFragmentDoc, user);
  const [chipSort, setSort] = useState<CollectorsSort>(CollectorsSort.TOP);

  function variables(sort: CollectorsSort): VariablesOf<typeof BackedArtistsDocument> {
    return {
      id: userData.id,
      pagination: {
        first: NUM_BACKED_ENTITIES_PREVIEW,
        sort:
          sort === 'top'
            ? {
                nftsCount: 'DESC',
              }
            : {
                firstNftCollectedDate: 'ASC',
              },
      },
    };
  }

  const { data, isLoading: loading } = useQuery(BackedArtistsDocument, {
    variables: variables(chipSort),
    staleTime: 0,
  });

  const backedArtistData = React.useMemo(
    () => data?.data.user?.backedArtists?.edges.map(v => v.node),
    [data],
  );

  const logError = useLogError();

  const showSeeAll = data?.data.user?.backedArtists.pageInfo.hasNextPage || false;

  if ((!backedArtistData || backedArtistData?.length === 0) && !loading) {
    return null;
  }

  return (
    <Container>
      <Heading>Artists Backed</Heading>
      <Wrapper>
        <CollectorsSortChips
          sort={chipSort}
          setSort={setSort}
          onMouseOver={sort => {
            prefetchQuery(BackedArtistsDocument, {
              variables: variables(sort),
              staleTime: Infinity,
            }).catch(error => {
              logError({
                action: MISCELLANEOUS_ERROR_ACTIONS.BACKED_ARTISTS_FETCH_ERROR,
                error,
                errorType: ERROR_TYPE.UNKNOWN,
                isArtistOnlyPage: false,
                level: 'warning',
                message: 'Calling prefetchQuery with BacckedArtistsDocument in CollectorsSortChips',
                feature: FEATURES.BACKED_ARTISTS,
                unindexedExtra: variables(sort),
              });
            });
          }}
        />
        {showSeeAll && (
          <SeeAllLink
            user={userData}
            path="/artists-backed"
            params={chipSort !== CollectorsSort.TOP ? { sort: chipSort } : null}
          />
        )}
      </Wrapper>

      <CollectorsSection
        data={backedArtistData}
        loading={loading && !data}
        sort={chipSort}
        variant="artistBacked"
      />
    </Container>
  );
};
